import { useState, useEffect, useRef } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { exchangeToken, syncActivities } from '../services/strava';
import { User } from '../types';

interface UseStravaOAuthReturn {
  isConnecting: boolean;
  progress: number;
}

const processedCodes = new Set<string>();

export const useStravaOAuth = (currentUser: User | null, toast: any): UseStravaOAuthReturn => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [progress, setProgress] = useState(0);
  const processingCode = useRef(false);

  useEffect(() => {
    const handleStravaCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');

      if (code && currentUser) {
        if (processedCodes.has(code)) {
          console.log(`[StravaOAuth] Code ${code} already processed. Skipping.`);
          return;
        }

        if (processingCode.current) {
          console.log(`[StravaOAuth] Already processing a code. Skipping.`);
          return;
        }

        processingCode.current = true;
        processedCodes.add(code);

        console.log(`[StravaOAuth] Starting exchange/sync for user ${currentUser.id} with code ${code.substring(0, 5)}...`);
        setIsConnecting(true);

        try {
          // Clear code from URL to prevent re-execution
          window.history.replaceState({}, document.title, window.location.pathname);

          await exchangeToken(code);

          // Update User status locally (backend handles tokens)
          const userRef = doc(db, 'users', currentUser.id);
          await updateDoc(userRef, {
            isConnectedStrava: true
          });

          // Trigger initial sync
          console.log('[StravaOAuth] Token exchange successful. Starting activity sync...');
          const result: any = await syncActivities();
          console.log('[StravaOAuth] Sync complete:', result);

          setProgress(100); // Complete animation
          await new Promise(r => setTimeout(r, 500)); // Short pause to let user see 100%

          toast.success(`Kết nối Strava thành công! Đã đồng bộ ${result.count || 0} hoạt động.`);
        } catch (error: any) {
          console.error('[StravaOAuth] Connection failed:', error);

          let errorMessage = error?.message || 'Kết nối Strava thất bại. Vui lòng thử lại sau.';

          // Check for specific "invalid code" error which suggests double-use or expiry
          const errorBody = JSON.stringify(error).toLowerCase();
          if (errorBody.includes('invalid') && errorBody.includes('code')) {
            errorMessage = 'Mã xác thực không hợp lệ hoặc đã hết hạn. Vui lòng thử kết nối lại.';
          }

          toast.error(errorMessage);
        } finally {
          setIsConnecting(false);
          processingCode.current = false;
        }
      }
    };

    handleStravaCallback();
  }, [currentUser, toast]);

  return { isConnecting, progress };
};
